/*
 * The three outside scripts the signed-out pages use, without a global of our
 * own: Google reCAPTCHA v3, Google Sign-In and the browser fingerprint.
 *
 * Nothing here keeps what it produces: tokens and ids go straight back to the
 * caller, and are never logged or stored.
 */

const loading = {};

/** Adds a script to the page once; resolves when it has run. */
function loadScript(src) {
    if (!loading[src]) {
        loading[src] = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => {
                delete loading[src];
                script.remove();
                reject(new Error('Could not load ' + src));
            };
            document.head.appendChild(script);
        });
    }
    return loading[src];
}

/* ---------- reCAPTCHA v3 ---------- */

/** Starts loading reCAPTCHA, so the first token does not wait for the script. */
export function loadRecaptcha(siteKey) {
    if (!siteKey) return Promise.reject(new Error('reCAPTCHA is not set up.'));
    return loadScript('https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(siteKey));
}

/**
 * A fresh token for one request. Tokens are good for two minutes and for one
 * check on the server, so ask right before each request.
 */
export async function recaptchaToken(siteKey, action) {
    await loadRecaptcha(siteKey);

    const grecaptcha = window.grecaptcha;
    if (!grecaptcha || typeof grecaptcha.ready !== 'function') {
        throw new Error('reCAPTCHA is not loaded.');
    }

    return new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
            grecaptcha.execute(siteKey, { action }).then(resolve, (error) => {
                reject(error || new Error('reCAPTCHA failed.'));
            });
        });
    });
}

/* ---------- Google Sign-In ---------- */

/**
 * Draws Google's button in `element`. `onCredential` is called with Google's
 * answer ({ credential }) when the visitor picks an account. `getNonce` gives
 * the one-time value the server handed this session (auth/ajax/google_nonce):
 * Google signs it into the credential, and the server accepts no credential
 * without it. Safe to call again for a new element (the button's place is
 * drawn again by Vue).
 */
export async function renderGoogleButton(element, clientId, text, onCredential, getNonce) {
    if (!element || !clientId || !getNonce) return;

    const [nonce] = await Promise.all([getNonce(), loadScript('https://accounts.google.com/gsi/client')]);
    if (!nonce) return;

    const ids = window.google && window.google.accounts && window.google.accounts.id;
    if (!ids || !element.isConnected) return;

    ids.initialize({ client_id: clientId, callback: onCredential, nonce });
    ids.renderButton(element, { type: 'standard', text, shape: 'pill' });
}

/* ---------- Browser fingerprint ---------- */

let devicePromise = null;

/**
 * The id of this browser, as the portal has always worked it out (the list of
 * a user's known devices holds these): Fingerprint2 from
 * assets/js/bkrd-fp2-device-id.js, which the page loads with a script tag
 * before the app. Worked out once, when they sign in or sign up (never on page load), for the account's devices
 * and its two-step check only.
 */
export function deviceId() {
    if (!devicePromise) {
        devicePromise = new Promise((resolve, reject) => {
            const report = () => {
                const Fingerprint2 = window.Fingerprint2;
                if (!Fingerprint2) {
                    reject(new Error('The browser check is not loaded.'));
                    return;
                }
                try {
                    Fingerprint2.get((components) => {
                        resolve(Fingerprint2.x64hash128(components.map((pair) => pair.value).join(), 31));
                    });
                } catch (error) {
                    reject(error);
                }
            };

            if (window.requestIdleCallback) window.requestIdleCallback(report);
            else setTimeout(report, 500);
        });
        // A failure is not kept: the next call tries again.
        devicePromise.catch(() => { devicePromise = null; });
    }
    return devicePromise;
}
